import { userErrorMessage } from '@wx-weiqi/contracts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  BoardPoint,
  BoardSnapshot,
  HomeworkSubmissionReplayResponse,
} from '@wx-weiqi/contracts';

import type { BoardViewport } from '../domain/board-geometry';
import {
  homeworkAnswerStoneLabels,
  homeworkVisibleStoneLabels,
} from '../domain/homework-replay-presentation';
import type { ClassroomMarkerMode } from '../domain/classroom-marker-display';
import {
  homeworkTracePathToStep,
  nextHomeworkTraceSteps,
  selectHomeworkTraceStep,
} from '../domain/homework-trace-navigation';
import type { HomeworkStore } from '../stores/homework-store';
import { BoardSurface } from './BoardSurface';
import { HomeworkMoveControls } from './HomeworkMoveControls';
import { useBoardWheelNavigation } from './useBoardWheelNavigation';

const TRACE_COLORS = Object.freeze([
  '#d53939',
  '#2563d9',
  '#14845f',
  '#8b4bc1',
  '#e07a1f',
  '#00899a',
  '#d64f91',
  '#6f5a3d',
]);

export type HomeworkSubmissionReplayMode = 'answer' | 'trace';

export function homeworkSubmissionReplayProgressLabel({
  mode,
  answerIndex,
  traceMoveNumber,
  choiceCount,
}: {
  readonly mode: HomeworkSubmissionReplayMode;
  readonly answerIndex: number;
  readonly traceMoveNumber: number;
  readonly choiceCount: number;
}): string {
  if (mode === 'answer') return `学生作答 · 第 ${answerIndex} 手`;
  if (choiceCount === 0) return `落子轨迹 · 第 ${traceMoveNumber} 手 · 分支结束`;
  return `落子轨迹 · 第 ${traceMoveNumber} 手 · ${choiceCount} 个下一手选点`;
}

export function HomeworkSubmissionReplayViewer({
  store,
  submissionId,
  mode,
  viewport,
  fallbackBoard,
  controlsContainer,
  presentation = 'classic',
  markerMode = 'ALL',
  customMarkerCount = 10,
  navigationBlocked = false,
}: {
  readonly store: HomeworkStore;
  readonly submissionId: string;
  readonly mode: HomeworkSubmissionReplayMode;
  readonly viewport: BoardViewport | null;
  readonly fallbackBoard: BoardSnapshot;
  readonly controlsContainer?: HTMLElement | null;
  readonly presentation?: 'classic' | 'studio';
  readonly markerMode?: ClassroomMarkerMode;
  readonly customMarkerCount?: number;
  readonly navigationBlocked?: boolean;
}) {
  const boardStageRef = useRef<HTMLDivElement | null>(null);
  const [loadedReplay, setReplay] = useState<{
    submissionId: string;
    data: HomeworkSubmissionReplayResponse['data'];
  } | null>(null);
  const replay = loadedReplay?.submissionId === submissionId ? loadedReplay.data : null;
  const [answerIndex, setAnswerIndex] = useState(0);
  const [tracePath, setTracePath] = useState<readonly number[]>([]);
  const [traceCursor, setTraceCursor] = useState(0);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setReplay(null);
    setLoadError(null);
    setAnswerIndex(0);
    setTracePath([]);
    setTraceCursor(0);
    void store
      .loadSubmissionReplay(submissionId)
      .then((next) => {
        if (!active) return;
        setReplay({ submissionId, data: next });
        setAnswerIndex(Math.max(0, next.frames.length - 1));
        setTracePath(homeworkTracePathToStep(next.trace.steps, next.trace.submittedStepIndex));
      })
      .catch((error: unknown) => {
        if (!active) return;
        setLoadError(userErrorMessage(error, '学生作答记录加载失败，请重试。'));
      });
    return () => {
      active = false;
      store.cancelSubmissionReplay();
    };
  }, [loadAttempt, store, submissionId]);

  const currentTraceStepIndex = traceCursor === 0 ? null : (tracePath[traceCursor - 1] ?? null);
  const currentTraceStep =
    currentTraceStepIndex === null ? undefined : replay?.trace.steps[currentTraceStepIndex];
  const initialTraceBoard = replay?.frames[0]?.board;
  const currentTraceBoard = currentTraceStep?.board ?? initialTraceBoard;
  const nextTraceSteps = useMemo(
    () => nextHomeworkTraceSteps(replay?.trace.steps ?? [], currentTraceStepIndex),
    [currentTraceStepIndex, replay],
  );
  const traceChoiceMarkers = useMemo(
    () =>
      nextTraceSteps.flatMap((step) =>
        step.move.kind === 'PLAY'
          ? [
              {
                point: step.move.point,
                color: TRACE_COLORS[(step.branchIndex - 1) % TRACE_COLORS.length]!,
                label: String(step.moveNumber),
              },
            ]
          : [],
      ),
    [nextTraceSteps],
  );
  const traceStoneLabels = useMemo(() => {
    if (replay === null || currentTraceBoard === undefined) return [];
    const labels = new Map<
      string,
      { readonly point: BoardPoint; readonly label: string; readonly color: 'BLACK' | 'WHITE' }
    >();
    tracePath.slice(0, traceCursor).forEach((stepIndex) => {
      const step = replay.trace.steps[stepIndex];
      if (step?.move.kind !== 'PLAY') return;
      const parentBoard =
        step.parentStepIndex === null
          ? initialTraceBoard
          : replay.trace.steps[step.parentStepIndex]?.board;
      const color = parentBoard?.currentPlayer;
      if (
        color === undefined ||
        currentTraceBoard.stones[step.move.point.y * currentTraceBoard.size + step.move.point.x] !==
          color
      ) {
        return;
      }
      labels.set(`${step.move.point.x}:${step.move.point.y}`, {
        point: step.move.point,
        label: String(step.moveNumber),
        color,
      });
    });
    return [...labels.values()];
  }, [currentTraceBoard, initialTraceBoard, replay, traceCursor, tracePath]);
  const answerStoneLabels = useMemo(
    () => homeworkAnswerStoneLabels(replay?.frames ?? [], answerIndex),
    [answerIndex, replay],
  );

  const chooseTraceStep = (stepIndex: number) => {
    if (replay === null) return;
    const selection = selectHomeworkTraceStep(
      replay.trace.steps,
      tracePath,
      traceCursor,
      stepIndex,
    );
    if (selection === null) return;
    setTracePath(selection.path);
    setTraceCursor(selection.cursor);
  };

  const chooseTracePoint = (point: BoardPoint) => {
    const step = nextTraceSteps.find(
      (candidate) =>
        candidate.move.kind === 'PLAY' &&
        candidate.move.point.x === point.x &&
        candidate.move.point.y === point.y,
    );
    if (step !== undefined) chooseTraceStep(step.stepIndex);
  };

  const canGoBack = mode === 'answer' ? answerIndex > 0 : traceCursor > 0;
  const canGoForward =
    mode === 'answer'
      ? replay !== null && answerIndex < replay.frames.length - 1
      : traceCursor < tracePath.length;
  const goBack = () => {
    if (mode === 'answer') {
      setAnswerIndex((current) => Math.max(0, current - 1));
      return;
    }
    setTraceCursor((current) => Math.max(0, current - 1));
  };
  const goForward = () => {
    if (mode === 'answer') {
      setAnswerIndex((current) => Math.min((replay?.frames.length ?? 1) - 1, current + 1));
      return;
    }
    setTraceCursor((current) => Math.min(tracePath.length, current + 1));
  };
  useBoardWheelNavigation(boardStageRef, {
    enabled: true,
    blocked: replay === null || navigationBlocked,
    canPrevious: canGoBack,
    canNext: canGoForward,
    context: `${submissionId}:${mode}`,
    onStep: (direction) => {
      if (direction < 0) goBack();
      else goForward();
    },
  });
  const progressLabel = homeworkSubmissionReplayProgressLabel({
    mode,
    answerIndex,
    traceMoveNumber: currentTraceStep?.moveNumber ?? 0,
    choiceCount: nextTraceSteps.length,
  });
  const displayedBoard =
    (mode === 'trace' ? currentTraceBoard : replay?.frames[answerIndex]?.board) ?? fallbackBoard;
  const controls = (
    <div className="homework-grading-replay-controls" aria-label="作答回放控制">
      <strong className={presentation === 'studio' ? 'grading-replay-description' : undefined}>
        {replay === null
          ? loadError === null
            ? '正在载入学生记录'
            : '学生记录加载失败'
          : progressLabel}
      </strong>
      {mode === 'trace' && nextTraceSteps.some((step) => step.move.kind === 'PASS') ? (
        <div className="homework-grading-replay-pass">
          {nextTraceSteps
            .filter((step) => step.move.kind === 'PASS')
            .map((step) => (
              <button
                key={step.stepIndex}
                type="button"
                onClick={() => chooseTraceStep(step.stepIndex)}
              >
                第 {step.moveNumber} 手：停一手
              </button>
            ))}
        </div>
      ) : null}
      {presentation === 'studio' ? (
        <HomeworkMoveControls
          value={mode === 'answer' ? answerIndex : traceCursor}
          max={mode === 'answer' ? Math.max(0, (replay?.frames.length ?? 1) - 1) : tracePath.length}
          disabled={replay === null}
          label={
            replay === null
              ? loadError === null
                ? '正在载入学生记录'
                : '学生记录加载失败'
              : progressLabel
          }
          onChange={mode === 'answer' ? setAnswerIndex : setTraceCursor}
        />
      ) : (
        <>
          <button type="button" disabled={replay === null || !canGoBack} onClick={goBack}>
            后退
          </button>
          <button type="button" disabled={replay === null || !canGoForward} onClick={goForward}>
            前进
          </button>
        </>
      )}
    </div>
  );

  return (
    <div
      className={`homework-grading-inline-replay${controlsContainer !== undefined ? ' homework-grading-inline-replay--docked' : ''}`}
      aria-label={mode === 'answer' ? '棋盘内查看学生作答' : '棋盘内查看落子轨迹'}
      aria-busy={replay === null && loadError === null}
    >
      <div
        className="homework-grading-board-stage homework-grading-inline-replay__stage"
        ref={boardStageRef}
      >
        <BoardSurface
          stoneAppearance={presentation === 'studio' ? 'shell' : 'classic'}
          {...(presentation === 'studio' ? { coordinateEdges: { top: true, left: true } } : {})}
          size={displayedBoard.size}
          stones={displayedBoard.stones}
          lastMove={null}
          stoneLabels={homeworkVisibleStoneLabels(
            mode === 'trace' ? traceStoneLabels : answerStoneLabels,
            markerMode,
            customMarkerCount,
          )}
          choiceMarkers={mode === 'trace' ? traceChoiceMarkers : []}
          viewport={viewport}
          {...(mode === 'trace' && replay !== null
            ? {
                isActionable: (point: BoardPoint) =>
                  nextTraceSteps.some(
                    (step) =>
                      step.move.kind === 'PLAY' &&
                      step.move.point.x === point.x &&
                      step.move.point.y === point.y,
                  ),
                onPoint: chooseTracePoint,
              }
            : {})}
        />
        {replay === null ? (
          <div
            className="homework-replay-load-state"
            role={loadError === null ? 'status' : 'alert'}
          >
            <strong>{loadError === null ? '正在载入棋盘' : '学生作答记录加载失败'}</strong>
            <span>{loadError ?? '请稍候。'}</span>
            {loadError !== null ? (
              <button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
                重新加载
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {controlsContainer === undefined
        ? controls
        : controlsContainer === null
          ? null
          : createPortal(controls, controlsContainer)}
    </div>
  );
}
